// Licensed to the Apache Software Foundation (ASF) under one
// or more contributor license agreements.  See the NOTICE file
// distributed with this work for additional information
// regarding copyright ownership.  The ASF licenses this file
// to you under the Apache License, Version 2.0 (the
// "License"); you may not use this file except in compliance
// with the License.  You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing,
// software distributed under the License is distributed on an
// "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
// KIND, either express or implied.  See the License for the
// specific language governing permissions and limitations
// under the License.

<template>
  <div class="form-layout" v-ctrl-enter="handleSubmit">
    <a-spin :spinning="loading">
      <p v-html="$t('message.update.userdata.visibility')"></p>
      <a-form
        :ref="formRef"
        :model="form"
        :rules="rules"
        @finish="handleSubmit"
        layout="vertical">
        <a-form-item name="visibility" ref="visibility">
          <template #label>
            <tooltip-label :title="$t('label.visibility')" :tooltip="apiParams.visibility ? apiParams.visibility.description : $t('label.visibility')"/>
          </template>
          <a-select
            v-model:value="form.visibility"
            v-focus="true"
            :placeholder="$t('label.visibility')">
            <a-select-option v-for="opt in visibilityOptions" :key="opt.value" :label="opt.label">
              {{ opt.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <div :span="24" class="action-button">
          <a-button @click="closeAction">{{ $t('label.cancel') }}</a-button>
          <a-button :loading="loading" ref="submit" type="primary" @click="handleSubmit">{{ $t('label.ok') }}</a-button>
        </div>
      </a-form>
    </a-spin>
  </div>
</template>

<script>
import { ref, reactive, toRaw } from 'vue'
import { postAPI } from '@/api'
import TooltipLabel from '@/components/widgets/TooltipLabel'

export default {
  name: 'UpdateUserDataVisibility',
  components: {
    TooltipLabel
  },
  props: {
    resource: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false
    }
  },
  beforeCreate () {
    this.apiParams = this.$getApiParams('updateUserData')
  },
  created () {
    this.initForm()
  },
  computed: {
    visibilityOptions () {
      const options = [
        { value: 'DOMAIN', label: this.$t('label.userdata.visibility.domain') },
        { value: 'ACCOUNT', label: this.$t('label.userdata.visibility.account') }
      ]
      // PUBLIC can only be set (and later changed) by root admins
      if (this.$store.getters.userInfo.roletype === 'Admin') {
        options.push({ value: 'PUBLIC', label: this.$t('label.userdata.visibility.public') })
      }
      return options
    }
  },
  methods: {
    initForm () {
      this.formRef = ref()
      this.form = reactive({
        visibility: this.resource.visibility || 'DOMAIN'
      })
      this.rules = reactive({
        visibility: [{ required: true, message: this.$t('message.error.required.input') }]
      })
    },
    handleSubmit (e) {
      if (e) {
        e.preventDefault()
      }
      if (this.loading) return
      this.formRef.value.validate().then(() => {
        const values = toRaw(this.form)
        this.loading = true
        postAPI('updateUserData', {
          id: this.resource.id,
          visibility: values.visibility
        }).then(() => {
          this.$message.success(this.$t('message.success.update.userdata.visibility'))
          this.$emit('refresh-data')
          this.closeAction()
        }).catch(error => {
          this.$notifyError(error)
        }).finally(() => {
          this.loading = false
        })
      }).catch(error => {
        this.formRef.value.scrollToField(error?.errorFields?.[0]?.name)
      })
    },
    closeAction () {
      this.$emit('close-action')
    }
  }
}
</script>

<style scoped lang="less">
  .form-layout {
    width: 80vw;

    @media (min-width: 600px) {
      width: 450px;
    }
  }
</style>
